import { type Registry as Services, service } from '@ember/service';
import RESTAdapter from '@ember-data/adapter/rest';
import type { AdapterPayload } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import type { HTTPMethod } from '@warp-drive/core-types/request';
import { underscore } from 'frontend-utils/utils/convert-case';
import { pluralize } from 'frontend-utils/utils/ember-data';
import type AjaxService from 'lumen/services/ajax';
import { Promise as RsvpPromise } from 'rsvp';

export default class ApplicationAdapter extends RESTAdapter {
  @service('ajax') declare ajaxService: AjaxService;
  @service declare errorTracking: Services['error-tracking'];

  host = '';
  namespace = '';

  get coalesceFindRequests(): boolean {
    return true;
  }

  constructor(...args: ConstructorParameters<typeof RESTAdapter>) {
    super(...args);

    this.host = this.ajaxService.host;
    this.namespace = this.ajaxService.namespace;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  ajax(url: string, type: HTTPMethod, options: JQueryAjaxSettings = {}) {
    return this.ajaxService.request<AdapterPayload>(
      url,
      // @ts-expect-error - Argument of type 'JQueryRequestInit | FetchRequestInit' is not assignable to parameter of type 'AJAXOptions | undefined
      super.ajaxOptions(url, type, options),
    );
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildURL(...args: Parameters<RESTAdapter['buildURL']>) {
    let url = super.buildURL(...args);

    if (url.charAt(url.length - 1) !== '/') {
      url += '/';
    }

    return url;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  findRecord(store: Store, type: ModelSchema, id: string, snapshot: Snapshot) {
    const typeKey = (snapshot.adapterOptions?.['typeKey'] ??
      type.modelName) as string;
    // @ts-expect-error: Expected 3 arguments, but got 4.
    const url = this.buildURL(typeKey, id, snapshot, 'findRecord');
    const query = this.buildQuery(snapshot);

    return this.ajax(url, 'GET', { data: query });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getQueryParameterByName(name: string) {
    const newName = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp(`[\\?&]${newName}=([^&#]*)`);
    const results = regex.exec(window.location.search);

    if (results === null || results?.[1] === undefined) {
      return '';
    }

    return decodeURIComponent(results[1].replace(/\+/g, ' '));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleQuery(
    store: Store,
    type: ModelSchema,
    query: Record<string, unknown> & {
      namespace?: string;
      typeKey?: string;
    },
  ) {
    this.namespace = this.ajaxService.namespace;
    if (query.namespace !== undefined && query.namespace !== null) {
      this.namespace = query.namespace;
      delete query.namespace;
    }

    let typeKey = type.modelName;
    if (query.typeKey) {
      typeKey = query.typeKey;
      delete query.typeKey;
    }

    return new RsvpPromise((resolve, reject) => {
      // @ts-expect-error: Expected 3 arguments, but got 1.
      const namespacedUrl = this.buildURL(typeKey);
      // Set the namespace back to the default namespace after the url has been built
      this.namespace = this.ajaxService.namespace;
      this.ajax(namespacedUrl, 'GET', {
        data: query,
      }).then(
        (resp) => {
          resolve(resp);
        },
        (resp) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (resp.status >= 500) {
            this.ajax(namespacedUrl, 'GET', {
              data: query,
            }).then(
              (resp2) => {
                resolve(resp2);
              },
              (resp2) => {
                reject(resp2);
              },
            );
          }

          reject(resp);
        },
      );
    }).catch((error) => {
      // We just add a breadcrumb instead of an error since we don't want to spam our error-tracking
      // when users perform actions that are expected to fail (f.e. navigating to a page without access rights)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.errorTracking.addBreadcrumb({ message: 'API error', data: error });

      return RsvpPromise.reject(error);
    });
  }

  pathForType(modelName: string): string {
    return pluralize(modelName);
  }

  // @ts-expect-error: Property 'query' in type 'ApplicationAdapter' is not assignable to the same property in base type 'BaseAdapter'.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  query(store: Store, type: ModelSchema, query: Record<string, unknown>) {
    return this.handleQuery(store, type, query);
  }

  // @ts-expect-error: Property 'queryRecord' in type 'ApplicationAdapter' is not assignable to the same property in base type 'BaseAdapter'.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  queryRecord(store: Store, type: ModelSchema, query: Record<string, unknown>) {
    return this.handleQuery(store, type, query);
  }

  updateRecord(
    store: Store,
    schema: ModelSchema,
    snapshot: Snapshot,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): RsvpPromise<any> {
    const { usePatch } = snapshot.adapterOptions || {};

    if (!usePatch) {
      // @ts-expect-error Property 'new' is missing in type 'Promise<AdapterPayload>' but required in type 'Promise<any>'
      return super.updateRecord(store, schema, snapshot);
    }

    const url = this.buildURL(
      schema.modelName,
      snapshot.id,
      snapshot,
      // @ts-expect-error Expected 3 arguments, but got 4
      'updateRecord',
    );

    const serializedSnapshot = snapshot.serialize({ includeId: true });
    const changedAttrs = snapshot.changedAttributes();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const payloadKey = store
      .serializerFor(snapshot.modelName)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .payloadKeyFromModelName(snapshot.modelName);

    const data = {
      [payloadKey]: {},
    };

    Object.keys(changedAttrs).forEach((key: string) => {
      const decamelizedKey = underscore(key);
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      data[payloadKey][decamelizedKey] = serializedSnapshot[decamelizedKey];
    });

    // @ts-expect-error: Incorrect type
    return this.ajax(url, 'PATCH', { data });
  }
}
